import { useForm } from "@tanstack/react-form";
import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { AuthError, useAuth } from "@/auth/AuthProvider";
import { attemptGoogleSignIn } from "@/auth/google-signin";
import { GoogleButton } from "@/components/google-button";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { loginSchema } from "@/lib/schemas";

export const Route = createFileRoute("/_public/login")({
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === "string" ? search.redirect : "/",
  }),
  component: LoginPage,
});

function LoginPage() {
  const { login, loginWithGoogle } = useAuth();
  const router = useRouter();
  const { redirect } = Route.useSearch();
  const [formError, setFormError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: { email: "", password: "" },
    validators: { onSubmit: loginSchema },
    onSubmit: async ({ value }) => {
      setFormError(null);
      try {
        await login(value.email, value.password);
        await router.navigate({ to: redirect });
      } catch (cause) {
        setFormError(describeLoginError(cause));
      }
    },
  });

  async function handleGoogle(idToken: string) {
    setFormError(null);
    const outcome = await attemptGoogleSignIn(loginWithGoogle, idToken);
    if (outcome.kind === "signed-in") {
      await router.navigate({ to: redirect });
    } else if (outcome.kind === "needs-establishment") {
      // Conta nova: falta o nome do negócio, que o Google não tem.
      await router.navigate({ to: "/cadastro", search: { google: outcome.idToken } });
    } else {
      setFormError(outcome.message);
    }
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        void form.handleSubmit();
      }}
    >
      <FieldGroup>
        <div className="flex flex-col gap-1 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Entrar</h1>
          <p className="text-sm text-muted-foreground">
            Acesse o painel do seu negócio.
          </p>
        </div>

        <form.Field name="email">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <FieldLabel htmlFor={field.name}>E-mail</FieldLabel>
              <Input
                id={field.name}
                name={field.name}
                type="email"
                autoComplete="email"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                aria-invalid={field.state.meta.errors.length > 0}
              />
              <FieldError errors={field.state.meta.errors} />
            </Field>
          )}
        </form.Field>

        <form.Field name="password">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <div className="flex items-center">
                <FieldLabel htmlFor={field.name}>Senha</FieldLabel>
                <Link
                  to="/esqueci-senha"
                  className="ml-auto text-sm underline-offset-4 hover:underline"
                >
                  Esqueci minha senha
                </Link>
              </div>
              <Input
                id={field.name}
                name={field.name}
                type="password"
                autoComplete="current-password"
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                aria-invalid={field.state.meta.errors.length > 0}
              />
              <FieldError errors={field.state.meta.errors} />
            </Field>
          )}
        </form.Field>

        {formError && (
          <p role="alert" className="text-sm text-destructive">
            {formError}
          </p>
        )}

        <form.Subscribe selector={(state) => state.isSubmitting}>
          {(isSubmitting) => (
            <Field>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Entrando…" : "Entrar"}
              </Button>
            </Field>
          )}
        </form.Subscribe>

        <FieldSeparator className="*:data-[slot=field-separator-content]:bg-card">
          ou
        </FieldSeparator>

        <GoogleButton onCredential={(token) => void handleGoogle(token)} />

        <FieldDescription className="text-center">
          Não tem conta?{" "}
          <Link to="/cadastro" className="underline underline-offset-4">
            Criar conta
          </Link>
        </FieldDescription>
      </FieldGroup>
    </form>
  );
}

/** Mensagem escolhida pelo `code` estável, nunca pelo texto vindo da API. */
function describeLoginError(cause: unknown): string {
  if (!(cause instanceof AuthError)) return "Não foi possível entrar. Tente novamente.";
  switch (cause.code) {
    case "network_error":
      return "Sem conexão com o servidor. Verifique sua internet.";
    case "invalid_credentials":
      return "E-mail ou senha incorretos.";
    case "rate_limited":
      return "Muitas tentativas. Aguarde um instante e tente de novo.";
    case "google_email_unverified":
      return "Seu e-mail do Google ainda não foi verificado.";
    case "invalid_google_token":
      return "A sessão do Google expirou. Tente novamente.";
    default:
      return "Não foi possível entrar. Tente novamente.";
  }
}
